import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useNavigate } from "react-router-dom";
import { Search, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import VerifiedBadge from "../components/VerifiedBadge";
import ElderBadge from "../components/ElderBadge";
import BoostedBadge from "../components/BoostedBadge";
import { useAuth } from "../context/AuthContext";
import CategorySelect from "../components/CategorySelect";
import TrendingCategoriesStrip from "../components/TrendingCategoriesStrip";
import CaulPriceTickerBadge from "../components/CaulPriceTickerBadge";

const CATEGORIES = [
  { name: "Readings", img: "https://images.unsplash.com/photo-1728241189719-ea4327c16eee?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { name: "Love Work", img: "https://images.unsplash.com/photo-1769791650061-3870c39b73b5?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { name: "Prosperity", img: "https://images.unsplash.com/photo-1769791650061-3870c39b73b5?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { name: "Protection", img: "https://images.unsplash.com/photo-1653307370781-a5b033bac658?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { name: "Classes", img: "https://images.unsplash.com/photo-1728241189719-ea4327c16eee?crop=entropy&cs=srgb&fm=jpg&q=85" },
  { name: "Initiations", img: "https://images.unsplash.com/photo-1653307370781-a5b033bac658?crop=entropy&cs=srgb&fm=jpg&q=85" },
];

export default function Marketplace() {
  const [services, setServices] = useState([]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", description: "", price: "", durationMin: 30, category: "Readings" });
  const nav = useNavigate();
  const { user } = useAuth();

  const load = async () => {
    const params = {};
    if (cat !== "All") params.category = cat;
    if (q) params.q = q;
    const { data } = await api.get("/services", { params });
    setServices(data);
  };

  const startEdit = (s) => {
    setEditId(s.id);
    setEditForm({
      title: s.title || "",
      description: s.description || "",
      price: String(s.price ?? ""),
      durationMin: s.durationMin || 30,
      category: s.category || "Readings",
    });
  };
  const cancelEdit = () => setEditId(null);
  const saveEdit = async () => {
    if (!editId) return;
    try {
      await api.patch(`/services/${editId}`, {
        title: editForm.title,
        description: editForm.description,
        price: parseFloat(editForm.price),
        durationMin: parseInt(editForm.durationMin),
        category: editForm.category,
      });
      setEditId(null);
      await load();
      toast.success("Service updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const removeService = async (s) => {
    if (!window.confirm(`Delete "${s.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/services/${s.id}`);
      await load();
      toast.success("Service deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, [cat]);

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <div className="label-mystic">The Bazaar</div>
          <h1 className="font-serif text-5xl mt-2">Marketplace</h1>
          <p className="text-foreground/70 mt-2">Sacred services from verified practitioners. Encrypted Stripe checkout.</p>
          <div className="mt-3">
            <CaulPriceTickerBadge />
          </div>
        </div>

        <form onSubmit={(e)=>{e.preventDefault();load();}} className="flex gap-2" data-testid="marketplace-search-form">
          <div className="flex-1 relative">
            <Search size={16} strokeWidth={1.25} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/50" />
            <input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search services..."
              className="w-full pl-10 bg-input border border-secondary/20 p-3 rounded-sm focus:outline-none focus:border-secondary"
              data-testid="marketplace-search-input" />
          </div>
          <button className="bg-primary text-primary-foreground px-6 rounded-sm uppercase tracking-wider text-xs" data-testid="marketplace-search-btn">Seek</button>
        </form>

        <TrendingCategoriesStrip selected={cat} onSelect={setCat} />

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[{name:"All",img:CATEGORIES[0].img}, ...CATEGORIES].map((c) => (
            <button key={c.name} onClick={() => setCat(c.name)}
              className={`relative overflow-hidden border ${cat===c.name?"border-primary":"border-secondary/15"} rounded-sm aspect-[5/3] group`}
              data-testid={`category-${c.name.toLowerCase().replace(' ','-')}`}>
              <img src={c.img} className="w-full h-full object-cover opacity-40 group-hover:opacity-60 transition" alt="" />
              <div className="absolute inset-0 flex items-end p-4 bg-gradient-to-t from-background/95 to-transparent">
                <span className="font-serif text-2xl">{c.name}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="space-y-4" data-testid="services-list">
          <div className="label-mystic">{services.length} {cat === "All" ? "Services" : cat}</div>
          {services.map((s) => {
            const isOwner = user && s.providerId === user.id;
            const inEdit = editId === s.id;
            return (
              <div key={s.id} className="parchment p-6 flex flex-col md:flex-row gap-6" data-testid={`market-svc-${s.id}`}>
                {inEdit ? (
                  <div className="w-full space-y-3" data-testid={`market-svc-edit-${s.id}`}>
                    <div className="label-mystic text-secondary">Editing service</div>
                    <input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} placeholder="Title" className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid={`market-svc-edit-title-${s.id}`} />
                    <textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} placeholder="Description" rows={3} className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid={`market-svc-edit-desc-${s.id}`} />
                    <div className="grid grid-cols-3 gap-3">
                      <input type="number" step="0.01" placeholder="Price USD" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} className="bg-input border border-secondary/20 p-3 rounded-sm" data-testid={`market-svc-edit-price-${s.id}`} />
                      <input type="number" placeholder="Duration min" value={editForm.durationMin} onChange={(e) => setEditForm({ ...editForm, durationMin: e.target.value })} className="bg-input border border-secondary/20 p-3 rounded-sm" data-testid={`market-svc-edit-duration-${s.id}`} />
                      <CategorySelect value={editForm.category} onChange={(v) => setEditForm({ ...editForm, category: v })} testId={`market-svc-edit-cat-${s.id}`} />
                    </div>
                    <div className="flex gap-2">
                      <button onClick={saveEdit} className="bg-primary text-primary-foreground px-5 py-2 rounded-sm uppercase tracking-wider text-xs" data-testid={`market-svc-edit-save-${s.id}`}>Save</button>
                      <button onClick={cancelEdit} className="border border-foreground/20 text-foreground/70 px-5 py-2 rounded-sm uppercase tracking-wider text-xs" data-testid={`market-svc-edit-cancel-${s.id}`}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="md:w-40 h-32 bg-secondary/5 border border-secondary/10 flex-shrink-0">
                      <img src={s.imageUrl || CATEGORIES.find(c=>c.name===s.category)?.img || CATEGORIES[0].img} className="w-full h-full object-cover" alt="" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="label-mystic">{s.category} · {s.durationMin}min</div>
                        {s.boosted && (
                          <BoostedBadge
                            providerName={s.provider?.businessName || s.providerUser?.name}
                            expiresAt={s.boostExpiresAt}
                            testId={`market-boosted-${s.id}`}
                          />
                        )}
                      </div>
                      <h3 className="font-serif text-2xl mt-1">{s.title}</h3>
                      <p className="text-foreground/75 text-sm mt-2 line-clamp-2">{s.description}</p>
                      <button onClick={() => nav(`/profile/${s.providerId}?from=marketplace${q ? `&q=${encodeURIComponent(q)}` : ""}`)} className="text-secondary hover:text-secondary/80 text-sm uppercase tracking-wider mt-3 inline-flex items-center gap-1.5" data-testid={`market-provider-${s.providerId}`}>
                        by {s.provider?.businessName}
                        <VerifiedBadge size={12} show={s.provider?.verified} />
                        <ElderBadge size={12} show={s.provider?.elder} index={s.provider?.elderIndex} />
                        →
                      </button>
                    </div>
                    <div className="text-right">
                      <div className="font-serif text-3xl text-secondary">${s.price}</div>
                      {isOwner ? (
                        <div className="mt-3 flex gap-2 items-end justify-end" data-testid={`market-svc-owner-actions-${s.id}`}>
                          <button onClick={() => startEdit(s)} className="inline-flex items-center gap-1.5 border border-secondary/40 text-secondary hover:bg-secondary/10 px-4 py-2 rounded-sm uppercase tracking-wider text-xs" data-testid={`market-svc-edit-btn-${s.id}`}>
                            <Edit size={12} strokeWidth={1.75} /> Edit
                          </button>
                          <button onClick={() => removeService(s)} className="inline-flex items-center gap-1.5 border border-destructive/40 text-destructive hover:bg-destructive/10 px-4 py-2 rounded-sm uppercase tracking-wider text-xs" data-testid={`market-svc-delete-btn-${s.id}`}>
                            <Trash2 size={12} strokeWidth={1.75} /> Delete
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => nav(`/profile/${s.providerId}?from=marketplace${q ? `&q=${encodeURIComponent(q)}` : ""}`)} className="mt-3 bg-primary text-primary-foreground px-5 py-2 rounded-sm uppercase tracking-wider text-xs" data-testid={`market-book-${s.id}`}>View</button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
          {services.length === 0 && <div className="parchment p-12 text-center text-foreground/60 font-serif text-2xl">No offerings here yet.</div>}
        </div>
      </div>
    </Layout>
  );
}
